import React, { useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { isLowPowerClient } from "../../lib/platform";

// Whisper-thin flowing "threads of light" that draw themselves in when the
// section enters the viewport. No imagery — pure brand canvas + type.
const THREADS = [
  { d: "M-60,150 C 420,40 1020,240 1500,120", o: 0.5, w: 1.1, delay: 0.0 },
  { d: "M-60,235 C 360,320 1080,110 1500,250", o: 0.35, w: 1, delay: 0.15 },
  { d: "M-60,330 C 460,240 1000,430 1500,300", o: 0.45, w: 1.2, delay: 0.3 },
  { d: "M-60,420 C 380,520 1060,300 1500,440", o: 0.3, w: 1, delay: 0.45 },
  { d: "M-60,510 C 440,410 1020,600 1500,470", o: 0.4, w: 1.1, delay: 0.6 },
  { d: "M-60,600 C 360,690 1080,470 1500,620", o: 0.25, w: 1, delay: 0.75 },
];

const THREAD_DEFS = (
  <defs>
    <linearGradient id="threadGrad" x1="0" y1="0" x2="1" y2="0">
      <stop offset="0%" stopColor="#6AA6FF" stopOpacity="0" />
      <stop offset="45%" stopColor="#6AA6FF" stopOpacity="1" />
      <stop offset="70%" stopColor="#A9CCFF" stopOpacity="0.9" />
      <stop offset="100%" stopColor="#6AA6FF" stopOpacity="0" />
    </linearGradient>
  </defs>
);
const THREAD_PATHS = THREADS.map((t, i) => (
  <motion.path
    key={i}
    d={t.d}
    fill="none"
    stroke="url(#threadGrad)"
    strokeWidth={t.w}
    strokeLinecap="round"
    initial={{ pathLength: 0, opacity: 0 }}
    whileInView={{ pathLength: 1, opacity: t.o }}
    viewport={{ once: true, amount: 0.4 }}
    transition={{ duration: 2.2, delay: t.delay, ease: [0.22, 1, 0.36, 1] }}
  />
));

// Desktop-only parallax wrappers — mounted only when !lite so mobile subscribes
// to no scroll events.
function ThreadsParallax({ targetRef }) {
  const { scrollYProgress } = useScroll({ target: targetRef, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [-40, 40]);
  return (
    <motion.svg style={{ y }} viewBox="0 0 1440 700" preserveAspectRatio="xMidYMid slice" className="pointer-events-none absolute inset-0 h-full w-full" aria-hidden="true">
      {THREAD_DEFS}{THREAD_PATHS}
    </motion.svg>
  );
}
function CopyParallax({ targetRef, children }) {
  const { scrollYProgress } = useScroll({ target: targetRef, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [50, -50]);
  return <motion.div style={{ y }} className="relative mx-auto w-full max-w-6xl px-6 lg:px-8">{children}</motion.div>;
}

export function WovenBand() {
  const ref = useRef(null);
  const [lite] = useState(() => isLowPowerClient());

  const copy = (
    <div className="max-w-xl">
      <p className="eyebrow">Woven together</p>
      <h2 className="mt-6 font-display text-4xl font-semibold leading-[1.06] tracking-tight text-mist sm:text-6xl">
        Every win is a thread.
        <br />
        <span className="text-gradient">Together, they hold.</span>
      </h2>
      <p className="mt-7 max-w-lg text-base leading-relaxed text-mist-muted sm:text-lg">
        One workout. One meal logged. One honest check-in. Alone they're small — woven with
        thousands of others, they become something no one builds alone.
      </p>
    </div>
  );

  return (
    <section ref={ref} id="woven" className="relative flex min-h-[80vh] items-center overflow-hidden bg-ink">
      {/* ambient glow */}
      <div className="pointer-events-none absolute left-1/2 top-1/2 h-[560px] w-[900px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-brand/10 blur-[160px]" />

      {/* animated threads of light */}
      {lite ? (
        <svg viewBox="0 0 1440 700" preserveAspectRatio="xMidYMid slice" className="pointer-events-none absolute inset-0 h-full w-full" aria-hidden="true">
          {THREAD_DEFS}{THREAD_PATHS}
        </svg>
      ) : (
        <ThreadsParallax targetRef={ref} />
      )}

      {/* soft vignette to seat the type */}
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_45%,rgba(11,13,18,0.55)_100%)]" />

      {lite ? (
        <div className="relative mx-auto w-full max-w-6xl px-6 lg:px-8">{copy}</div>
      ) : (
        <CopyParallax targetRef={ref}>{copy}</CopyParallax>
      )}
    </section>
  );
}
